import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CampoFecha } from "@/components/ds";
import { Label } from "@/components/ui/label";
import {
  FormDrawerBody,
  FormDrawerContent,
  FormDrawerDescription,
  FormDrawerFooter,
  FormDrawerHeader,
  FormDrawerTitle,
  formDrawerBodyClass,
} from "@/components/ds/FormDrawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Loader2, Save } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { formatNombreArea } from "@/lib/area";
import type { AreaSummary, Contrato, TipoContrato } from "./contratos-types";

const TIPOS: TipoContrato[] = ["Faena", "Proyecto", "Servicio", "Otro"];

interface Mandante {
  relacion_id: number;
  estado_relacion: "pendiente" | "activa" | "rechazada";
  empresa: { id: number; rut: string; nombre: string };
}

interface ContratoFormDrawerProps {
  mode: "create" | "edit";
  contrato?: Contrato;
  onSaved: () => void;
}

export function ContratoFormDrawer({ mode, contrato, onSaved }: ContratoFormDrawerProps) {
  const [mandantes, setMandantes] = useState<Mandante[]>([]);
  const [isLoadingMandantes, setIsLoadingMandantes] = useState(mode === "create");
  const [isLoadingAreas, setIsLoadingAreas] = useState(true);
  const [areas, setAreas] = useState<AreaSummary[]>([]);
  const [areasIds, setAreasIds] = useState<string[]>(
    contrato?.areas?.map((a) => String(a.id)) ?? [],
  );
  const [relacionId, setRelacionId] = useState<string>(
    contrato ? String(contrato.relacion_empresa.id) : "",
  );
  const [nombreReferencia, setNombreReferencia] = useState(contrato?.nombre_referencia ?? "");
  const [tipoContrato, setTipoContrato] = useState<TipoContrato>(contrato?.tipo_contrato ?? "Otro");
  const [fechaInicio, setFechaInicio] = useState(contrato?.fecha_inicio ?? "");
  const [fechaTermino, setFechaTermino] = useState(contrato?.fecha_termino ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ data: AreaSummary[] }>("/areas?all=1");
        if (cancelled) return;
        setAreas(res.data);
      } catch {
        toast.error("No se pudieron cargar las áreas.");
      } finally {
        if (!cancelled) setIsLoadingAreas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mode !== "create") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ data: Mandante[] }>("/comercial/mandantes");
        if (cancelled) return;
        setMandantes(res.data.filter((m) => m.estado_relacion === "activa"));
      } catch {
        toast.error("No se pudo cargar el listado de mandantes.");
      } finally {
        if (!cancelled) setIsLoadingMandantes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombreReferencia.trim()) {
      toast.error("El nombre del contrato es requerido.");
      return;
    }
    if (mode === "create" && !relacionId) {
      toast.error("Elige un mandante.");
      return;
    }

    const payload = {
      nombre_referencia: nombreReferencia.trim(),
      tipo_contrato: tipoContrato,
      fecha_inicio: fechaInicio || null,
      fecha_termino: fechaTermino || null,
      areas_ids: areasIds.map(Number),
    };

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        await api.post("/comercial/contratos", {
          ...payload,
          relacion_empresa_id: Number(relacionId),
        });
        toast.success("Contrato creado.");
      } else if (contrato) {
        await api.put(`/comercial/contratos/${contrato.id}`, payload);
        toast.success("Contrato actualizado.");
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
      } else if (err instanceof ApiError && err.status === 403) {
        toast.error("No tienes permiso para esta acción.");
      } else {
        toast.error("No se pudo guardar el contrato.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === "create" && isLoadingMandantes) {
    return (
      <FormDrawerContent>
        <FormDrawerHeader>
          <FormDrawerTitle>Nuevo contrato</FormDrawerTitle>
        </FormDrawerHeader>
        <div className="flex flex-1 items-center justify-center p-4 py-8 md:p-[18px]">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </FormDrawerContent>
    );
  }

  if (mode === "create" && mandantes.length === 0) {
    return (
      <FormDrawerContent>
        <FormDrawerHeader>
          <FormDrawerTitle>Nuevo contrato</FormDrawerTitle>
          <FormDrawerDescription>
            Para crear un contrato con un mandante, primero tienes que invitarlo y que acepte la
            vinculación.
          </FormDrawerDescription>
        </FormDrawerHeader>
        <FormDrawerBody>
          <div className="rounded-lg border bg-muted/40 p-4 text-sm">
            No tienes mandantes vinculados todavía.
          </div>
        </FormDrawerBody>
        <FormDrawerFooter>
          <Link href="/comercial/mandantes">
            <Button>Ir a Mandantes</Button>
          </Link>
        </FormDrawerFooter>
      </FormDrawerContent>
    );
  }

  const areasOptions = areas.map((a) => ({ value: String(a.id), label: formatNombreArea(a) }));

  return (
    <FormDrawerContent>
      <FormDrawerHeader>
        <FormDrawerTitle>{mode === "create" ? "Nuevo contrato" : "Editar contrato"}</FormDrawerTitle>
        <FormDrawerDescription>
          {mode === "create"
            ? "Asociá el contrato a uno de tus mandantes activos."
            : "Actualiza los datos del contrato. Las fechas son referenciales."}
        </FormDrawerDescription>
      </FormDrawerHeader>

      <form onSubmit={submit} className={formDrawerBodyClass}>
        {/* data-tour="contrato-mandante" — primer campo visible en modo create;
            el rAF poll de la guía busca este wrapper para saber que el diálogo montó. */}
        <div className="space-y-2" data-tour="contrato-mandante">
          <Label htmlFor="mandante">Mandante</Label>
          <Select
            value={relacionId}
            onValueChange={setRelacionId}
            disabled={mode === "edit" || isSubmitting}
          >
            <SelectTrigger id="mandante">
              <SelectValue placeholder="Elige un mandante" />
            </SelectTrigger>
            <SelectContent>
              {mode === "edit" && contrato ? (
                <SelectItem value={String(contrato.relacion_empresa.id)}>
                  {contrato.es_base
                    ? "Operación interna"
                    : contrato.relacion_empresa.mandante.nombre}
                </SelectItem>
              ) : (
                mandantes.map((m) => (
                  <SelectItem key={m.relacion_id} value={String(m.relacion_id)}>
                    {m.empresa.nombre}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="nombre">Nombre del contrato</Label>
          <Input
            id="nombre"
            value={nombreReferencia}
            onChange={(e) => setNombreReferencia(e.target.value)}
            placeholder="Ej: Servicio mantención flota"
            disabled={isSubmitting}
            autoFocus={mode === "edit"}
          />
        </div>

        {/* data-tour="contrato-tipo" — selector Faena/Proyecto/Servicio/Otro */}
        <div className="space-y-2" data-tour="contrato-tipo">
          <Label htmlFor="tipo">Tipo</Label>
          <Select
            value={tipoContrato}
            onValueChange={(v) => setTipoContrato(v as TipoContrato)}
            disabled={isSubmitting}
          >
            <SelectTrigger id="tipo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIPOS.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* data-tour="contrato-areas" — selector de áreas de la faena */}
        <div className="space-y-2" data-tour="contrato-areas">
          <Label>Áreas</Label>
          <MultiSelect
            options={areasOptions}
            value={areasIds}
            onChange={setAreasIds}
            placeholder={
              isLoadingAreas
                ? "Cargando áreas..."
                : areasOptions.length === 0
                  ? "No hay áreas creadas todavía"
                  : "Selecciona áreas"
            }
            emptyText="Sin áreas que coincidan."
            disabled={isSubmitting || isLoadingAreas || areasOptions.length === 0}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="fecha-inicio">Fecha inicio</Label>
            <CampoFecha
              id="fecha-inicio"
              value={fechaInicio}
              onChange={setFechaInicio}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fecha-termino">Fecha término</Label>
            {/* Sin `min={fechaInicio}` a propósito: un contrato ya cargado
                puede tener fechas raras y bloquearlas acá impediría editar el
                resto del formulario. Validar el orden inicio/término es su
                propio cambio, con la regla también en el backend. */}
            <CampoFecha
              id="fecha-termino"
              value={fechaTermino}
              onChange={setFechaTermino}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <FormDrawerFooter>
          <Button type="submit" disabled={isSubmitting} className="gap-2" data-tour="contrato-guardar">
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {mode === "create" ? "Crear contrato" : "Guardar cambios"}
          </Button>
        </FormDrawerFooter>
      </form>
    </FormDrawerContent>
  );
}
